import React from 'react';
import { useForkRef } from '@highlight-ui/utils-hooks';
import { noop, OutlineVariant } from '@highlight-ui/utils-commons';

import { SelectOption, SelectVariant } from '../types';

export type PopoverTriggerProps<T extends SelectOption> = {
  /** Should component be enabled or disabled */
  disabled: boolean;
  /** Label being shown by default on the trigger button */
  label: string;
  /** Id passed to the trigger */
  triggerId?: string;
  /** Sets the color of the input's border */
  outline?: OutlineVariant;
  /** Automatically focus the Select's trigger on mount */
  autoFocus?: boolean;
  /** `inline` when used outside forms and `full-width` when used inside forms. */
  variant: SelectVariant;
  /** passed state for visibility of content */
  visible: boolean;
  /** The callback to execute when interacting with the trigger */
  onClick: typeof noop;
  /** trigger ref to correctly position dropdown */
  selectRef: React.Ref<HTMLElement>;
  /** The callback to clear all selected options */
  onClear?: (evt: React.MouseEvent | React.KeyboardEvent) => void;
  /** The callback to remove 1 option */
  onRemove?: (option: T) => void;
  /** The callback for when the list's visibility changes */
  setListVisibility: (value: boolean) => void;
  /** custom ref passed from popover */
  triggerRef: React.Ref<HTMLElement>;
  /** Set to `true` when you have a `multi-select` scenario */
  multiple?: boolean;
  /** Array of all selected options */
  selectedOptions: T[];
  /** Sets a callback for onKeyDown event when a key is pressed. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /** The underlying component for the trigger */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: React.ReactNode | ((props: any) => JSX.Element);
};

const PopoverTrigger = <T extends SelectOption>({
  children,
  triggerRef,
  ...passedProps
}: PopoverTriggerProps<T>) => {
  const customRef = useForkRef([triggerRef, null]);

  // Backwards compatibility: custom method used to render a bespoke trigger
  if (typeof children === 'function') {
    const editedProps = {
      ...passedProps,
      ref: customRef,
    };

    return children(editedProps);
  }

  if (children) {
    const child = React.Children.only(children);
    return React.cloneElement(child as React.ReactElement, {
      ref: customRef,
      ...passedProps,
    });
  }

  return null;
};

PopoverTrigger.displayName = 'Trigger';

export default PopoverTrigger;
